import {put, call} from 'redux-saga/effects';
import RedFlag from "../../entities/redFlag";
import * as Actions from "../actions/redFlagActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RedFlagAction = {
    redFlagId: string;
    redFlag: RedFlag;
    redFlags: RedFlag[];
};

export function* fetchOneRedFlag(action: RedFlagAction) {
	try {
		const redFlag: RedFlag = yield call(api.findOne, 'redFlag', action.redFlagId);

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_FETCH_ONE_SUCCESS, redFlagId: action.redFlagId, redFlag});
	}
	catch(err) {
		yield put({type: Actions.REDFLAG_FETCH_ONE_FAILURE, redFlagId: action.redFlagId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RedFlagAction} action
 */
export function* saveRedFlag(action: RedFlagAction) {
    // console.debug('⮂ REDFLAG_SAVE saga', action.redFlag?.id);
    
	if (action.redFlag === undefined || action.redFlag === null) {
		console.error('saveRedFlag received a null');
		throw new Error('saveRedFlag received a null');
	}

	if (!(action.redFlag instanceof RedFlag)) {
		action.redFlag = new RedFlag(action.redFlag);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlag', action.redFlag);
		if (addResult.isErr()) {
			console.error(`⮂ saveRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAG_SAVE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_SAVE, redFlag: action.redFlag});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'redFlag', action.redFlag.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.REDFLAG_SAVE_FAILURE, redFlag: action.redFlag, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {RedFlagAction} action
 * @param {RedFlag[]} action.redFlags
 */
export function* saveManyRedFlags(action: RedFlagAction) {
    // console.debug('⮂ REDFLAG_SAVE_MANY saga', action.redFlags.map(redFlag => redFlag.id).join(','));
    
    if (!Array.isArray(action.redFlags)) {
        console.error('saveManyRedFlags expected an array, received', action.redFlags);
        throw new Error('saveManyRedFlags expected an array');
    }

	try {
		const toSave: RedFlag[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.redFlags.length; i++) {
			const redFlag = action.redFlags[i];
			redFlag.persisted = false;
			toSave.push(redFlag);
		}

		const addResult = yield call(idb.add, 'redFlag', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyRedFlags: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAG_SAVE_MANY_FAILURE, redFlags: action.redFlags, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_SAVE_MANY, redFlags: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.REDFLAG_SAVE_MANY_FAILURE, redFlags: action.redFlags, status: 500, err});

		console.error(`Error saving ${action.redFlags.length} redFlags to database`, err);
	}
}

/**
 * @param {RedFlagAction} action
 */
export function* removeRedFlag(action: RedFlagAction) {
    const userId = getCurrentUserId();
	if (!userId || action.redFlag === undefined || action.redFlag === null || !(action.redFlag instanceof RedFlag)) {
			console.error('removeRedFlag expected type RedFlag, received', action.redFlag);
			throw new Error('removeRedFlag expected type RedFlag');
		}

	try {
		// Mark as deleted
		action.redFlag.status = 'D';
		action.redFlag.addedOn = newHRTimestamp();
		action.redFlag.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlag', action.redFlag);
		if (addResult.isErr()) {
			console.error(`⮂ removeRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAG_REMOVE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_REMOVE, redFlag: action.redFlag});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.REDFLAG_REMOVE_FAILURE, redFlag: action.redFlag, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this RedFlag.
	*
	* @param {RedFlagAction} action
	*/
export function* restoreRedFlag(action: RedFlagAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRedFlag expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.redFlag === undefined || action.redFlag === null) {
        console.error('restoreRedFlag expected a RedFlag, received null');
        throw new Error('restoreRedFlag expected a RedFlag, received null');
    }
    else if (!(action.redFlag instanceof RedFlag)) action.redFlag = new RedFlag(action.redFlag); 
    
	try {
		// Mark as restored
		action.redFlag.status = 'U';
		action.redFlag.addedOn = newHRTimestamp();
		action.redFlag.addedBy = userId;
		action.redFlag.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'redFlag', action.redFlag);
		if (addResult.isErr()) {
			console.error(`⮂ restoreRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAG_RESTORE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_RESTORE, redFlag: action.redFlag});
	}
	catch(err) {
		yield put({type: Actions.REDFLAG_RESTORE_FAILURE, redFlag: action.redFlag, status: 500, err});

		console.error('Error restoring from database', err);
	}
}